<template>
  <div class="max-h-[calc(100dvh-3rem)] overflow-y-auto p-5 xs:p-7">
    <form v-if="!isUnlocked" @submit.prevent="unlock">
      <h1 class="text-xl font-semibold text-charcoal-700">Account tools</h1>

      <u-form-group class="mt-5" label="Access code" :error="invalidCode ? 'Invalid code.' : null">
        <u-input
          v-model="accessCode"
          type="password"
          autocomplete="off"
          autofocus
          @update:model-value="invalidCode = false"
        />
      </u-form-group>

      <div class="mt-6 flex justify-end gap-3">
        <u-button type="button" color="charcoal" variant="soft" @click="modal.close()">Cancel</u-button>
        <u-button type="submit">Continue</u-button>
      </div>
    </form>

    <div v-else>
      <h1 class="text-xl font-semibold text-charcoal-700">Account tools</h1>
      <p class="mt-2 break-words text-sm text-charcoal-400">
        {{ session.currentUser?.name }} ({{ accountId }})
        <span v-if="isManagedAccount" class="font-semibold">— Managing this account</span>
      </p>

      <section class="mt-6 rounded border border-gray-200 p-4">
        <h2 class="font-semibold text-charcoal-700">Onboarding and welcome prompts</h2>
        <p class="mt-1 text-sm text-charcoal-400">
          Changes are staged until you save. Unset clears a value. Digitization state and your actual landing page preference are not changed.
        </p>

        <p v-if="loading" class="mt-5 text-sm" role="status">Loading current onboarding state…</p>
        <div v-else-if="loadError" class="mt-5">
          <p class="text-sm text-red-600" role="alert">{{ loadError }}</p>
          <u-button class="mt-3" color="charcoal" variant="soft" @click="loadEditor">Retry</u-button>
        </div>

        <template v-else>
          <p v-if="replacing" class="mt-4 text-sm text-red-600" role="status">
            Reset prompts is staged. Saving will clear every onboarding value, except values you set below.
          </p>
          <fieldset :disabled="busy" class="mt-5 space-y-5">
            <u-form-group v-for="field in fields" :key="field.key" :label="field.label" :error="fieldError(field)" class="break-words">
              <p class="mb-1 break-all text-xs text-charcoal-400">{{ field.key }}</p>
              <u-select
                v-if="field.type === 'boolean' || field.type === 'response'"
                :model-value="choiceValue(field)"
                :options="choiceOptions(field)"
                value-attribute="value"
                option-attribute="label"
                :disabled="busy"
                @update:model-value="setChoice(field, $event)"
              />
              <div v-else-if="field.type === 'string' || field.type === 'number'" class="flex items-start gap-2">
                <div class="min-w-0 flex-1">
                  <u-input
                    :model-value="field.unset ? '' : field.value"
                    :type="field.type === 'number' ? 'number' : 'text'"
                    :step="field.type === 'number' ? 'any' : undefined"
                    :disabled="busy"
                    @update:model-value="setInput(field, $event)"
                  />
                  <p v-if="field.unset" class="mt-1 text-xs text-charcoal-400">Unset</p>
                </div>
                <u-button color="charcoal" variant="soft" :disabled="busy || field.unset" @click="field.unset = true">Unset</u-button>
              </div>
              <div v-else-if="field.type === 'structured'">
                <p class="text-xs text-charcoal-400">Read-only. Reset all will clear this value.</p>
                <pre class="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-all rounded bg-gray-50 p-2 text-xs">{{ field.unset ? 'Unset' : JSON.stringify(field.value, null, 2) }}</pre>
              </div>
              <u-select
                v-else
                model-value="unset"
                :options="typeOptions"
                value-attribute="value"
                option-attribute="label"
                :disabled="busy"
                @update:model-value="setType(field, $event)"
              />
            </u-form-group>
          </fieldset>

          <p v-if="saveError" class="mt-4 text-sm text-red-600" role="alert">{{ saveError }}</p>
          <div class="mt-5 flex flex-wrap items-center justify-between gap-3">
            <u-button color="charcoal" variant="soft" :disabled="busy" @click="resetAll">Reset prompts</u-button>
            <div class="flex gap-3">
              <u-button color="charcoal" variant="soft" :disabled="busy || !dirty" @click="cancelChanges">Cancel changes</u-button>
              <u-button :loading="saving" :disabled="busy || !dirty || !valid" @click="save">Save changes</u-button>
            </div>
          </div>
        </template>
      </section>

      <div class="mt-6 space-y-3">
        <h2 class="font-semibold text-charcoal-700">Other account tools</h2>
        <p v-if="dirty" class="text-sm text-charcoal-400">Save or cancel your edits before using these tools.</p>
        <div class="rounded border border-gray-200 p-4">
          <h3 class="font-semibold text-charcoal-700">Reset initial onboarding</h3>
          <p class="mt-1 text-sm text-charcoal-400">Reset account-type selection only. Initial onboarding will start on the next navigation. Welcome prompts and the Engage prompt response are unchanged.</p>
          <u-button
            class="mt-4"
            color="charcoal"
            variant="soft"
            :loading="resetAction === 'initial-onboarding'"
            :disabled="toolsDisabled"
            @click="resetInitialOnboarding"
          >
            Reset initial onboarding
          </u-button>
        </div>
        <div class="rounded border border-gray-200 p-4">
          <h3 class="font-semibold text-charcoal-700">Reset all</h3>
          <p class="mt-1 text-sm text-charcoal-400">Reset initial onboarding, all welcome prompts, and onboarding app-state. Initial onboarding will start on the next navigation. Digitization state and other preferences are unchanged.</p>
          <u-button
            class="mt-4"
            color="heroblue"
            :loading="resetAction === 'account-setup'"
            :disabled="toolsDisabled"
            @click="restartAccountSetup"
          >
            Reset all
          </u-button>
        </div>
      </div>

      <div class="mt-6 flex justify-end">
        <u-button color="charcoal" variant="soft" :disabled="busy" @click="close">Close</u-button>
      </div>
    </div>
  </div>
</template>

<script setup>
  const props = defineProps({
    modal: Object
  });

  const RESET_UNLOCK_CODE = 'foreverrules';
  const accessCode = ref('');
  const invalidCode = ref(false);
  const isUnlocked = ref(false);
  const resetAction = ref(null);
  const session = useSessionStore();
  const appState = useAppStateStore();
  const preferences = usePreferencesStore();
  const accountId = session.currentUserId;
  const isManagedAccount = session.isImpersonating;
  const loading = ref(false);
  const saving = ref(false);
  const loadError = ref(null);
  const saveError = ref(null);
  const {
    fields, replacing, dirty, valid, load, cancel, resetAll,
    setType, setChoice, setInput, fieldError, submission
  } = useOnboardingStateEditor();
  const busy = computed(() => loading.value || saving.value || Boolean(resetAction.value));
  const toolsDisabled = computed(() => busy.value || dirty.value || Boolean(loadError.value));
  const typeOptions = [
    {label: 'Unset — choose a value type to edit', value: 'unset'},
    {label: 'Boolean', value: 'boolean'},
    {label: 'Text', value: 'string'},
    {label: 'Number', value: 'number'}
  ];
  let disposed = false;

  const isCurrent = () => !disposed && String(session.currentUserId) === String(accountId)
    && session.isImpersonating === isManagedAccount;

  function close() {
    disposed = true;
    cancel();
    props.modal.close();
  }

  watch(() => [session.currentUserId, session.isImpersonating], () => {
    if (!isCurrent()) close();
  }, {flush: 'sync'});
  watch(() => props.modal.isOpen, isOpen => {
    if (isOpen === false) disposed = true;
  });
  onScopeDispose(() => { disposed = true; });

  function unlock() {
    if (accessCode.value === RESET_UNLOCK_CODE) {
      isUnlocked.value = true;
      loadEditor();
      return;
    }

    invalidCode.value = true;
  }

  async function loadEditor() {
    if (!isCurrent()) return;
    loading.value = true;
    loadError.value = null;
    try {
      const [data, prefs] = await Promise.all([
        appState.fetchAppState({userId: accountId, throwOnError: true}),
        preferences.getCurrentUserPreferences({userId: accountId})
      ]);
      if (isCurrent()) load(data.onboarding, prefs);
    } catch (error) {
      if (isCurrent()) loadError.value = 'Could not load current onboarding state. Please retry.';
    } finally {
      if (isCurrent()) loading.value = false;
    }
  }

  function choiceValue(field) {
    if (field.unset) return 'unset';
    if (field.type === 'boolean' && typeof field.value === 'boolean') return String(field.value);
    if (field.type === 'response' && Object.values(ENGAGE_LANDING_PROMPT.responses).includes(field.value)) return field.value;
    return 'stored';
  }

  function choiceOptions(field) {
    const options = [{label: 'Unset', value: 'unset'}];
    if (field.type === 'boolean') {
      options.push({label: 'True', value: 'true'}, {label: 'False', value: 'false'});
    } else {
      options.push(
        {label: 'Accepted', value: ENGAGE_LANDING_PROMPT.responses.accepted},
        {label: 'Declined', value: ENGAGE_LANDING_PROMPT.responses.declined}
      );
    }
    if (choiceValue(field) === 'stored') options.push({label: `Stored value: ${JSON.stringify(field.value)}`, value: 'stored'});
    return options;
  }

  function cancelChanges() {
    cancel();
    saveError.value = null;
  }

  async function save() {
    if (!isCurrent() || busy.value || !dirty.value || !valid.value) return;
    saving.value = true;
    saveError.value = null;
    try {
      const draft = submission();
      if (Object.keys(draft.preferences).length) {
        await preferences.updateCurrentUserPreferences(draft.preferences, {userId: accountId});
      }
      if (!isCurrent()) return;
      if (draft.replace || Object.keys(draft.changes).length) {
        await appState.updateOnboardingState({userId: accountId, changes: draft.changes, replace: draft.replace});
      }
      if (!isCurrent()) return;
      load(appState.appState.onboarding, preferences.currentUserPreferences);
      useSuccessToast().add({description: 'Onboarding and welcome prompts saved.'});
    } catch (error) {
      if (isCurrent()) saveError.value = 'Could not save all changes. Some may already be saved. Your edits are kept; retry to finish saving.';
    } finally {
      if (isCurrent()) saving.value = false;
    }
  }

  async function runReset(action, reset) {
    if (!isCurrent() || toolsDisabled.value) return;
    resetAction.value = action;
    try {
      await reset();
      if (!isCurrent()) return;
      useSuccessToast().add({description: 'Account settings reset.'});
      close();
    } catch (error) {
      if (isCurrent()) {
        useErrorToast().add();
        await loadEditor();
      }
    } finally {
      resetAction.value = null;
    }
  }

  function resetInitialOnboarding() {
    return runReset('initial-onboarding', () => session.updateCurrentUser({account_type: USER_ACCOUNT_TYPES.unspecified.value}));
  }

  function restartAccountSetup() {
    return runReset('account-setup', async () => {
      await appState.updateOnboardingState({userId: accountId, changes: {}, replace: true});
      if (!isCurrent()) return;
      await preferences.clearOnboardingSettings({userId: accountId});
      if (!isCurrent()) return;
      await session.updateCurrentUser({account_type: USER_ACCOUNT_TYPES.unspecified.value});
    });
  }
</script>
